<template>
  <panel
    class="case-questions"
    color="blue-3"
    title="Dialogue"
    scroll-mode="content"
    aria-label="Section Title"
    data-testid="dialogue-header"
  >
    <template v-slot:title>
      <div v-if="isAiAvailable" class="ai-switch-container">
        <toggle-switch
          class="global-toggle md-primary ui-theme-3"
          :value="isAiEnabled"
          on-color="#7D30C9"
          :readonly="readonly"
          aria-label="Toggle AI Dialogue"
          data-testid="ai-dialogue-toggle"
          @toggle="value => updateAiEnabled(value)"
        />
        <div class="ai-description-container" aria-label="AI Dialogue Description" data-testid="ai-dialogue-description">
          <label>AI Patient Dialogue</label>
          <span>
            Enables patient AI dialogue. Retains previous questions and answers if disabled.
          </span>
        </div>
      </div>
    </template>

    <div class="tabs-container" data-testid="dialogue-tabs-container">
      <template v-if="!isAiEnabled">
        <tab-dropdown
          :prefix="caseConf.patientName ? '' : 'Patient'"
          :label="caseConf.patientName"
          :icon="require('@/assets/icons/patient.svg')"
          aria-label="Patient Dialogue Options"
          data-testid="patient-dialogue-tab-dropdown"
          :items="patientItems"
          :selected="state.activeId === 'PATIENT'"
          :show-rename="!readonly"
          @selectItem="onSelectPatientItem"
          @renameLabel="value => onUpdatePatientLabel(value)"
        />
        <tab-dropdown
          v-for="npc in activeNpcs(caseConf)"
          :key="npc.key"
          :prefix="npc.name ? '' : npcLabel(npc)"
          :label="npc.name"
          :icon="require('@/assets/icons/npc.svg')"
          :items="npcItems"
          :aria-label="`${npcLabel(npc)} Npc Dialogue Options`"
          data-testid="npc-dialogue-options"
          :selected="state.activeId === npc.key"
          :show-rename="!readonly"
          @selectItem="itemKey => onSelectNpcItem(npc.key, itemKey)"
          @renameLabel="value => onUpdateNpcLabel(npc, value)"
        />
      </template>

      <template v-if="isAiEnabled">
        <tab-dropdown
          v-if="isAiEnabled"
          :prefix="'AI ' + (caseConf.patientName ? '' : 'Patient')"
          :label="caseConf.patientName"
          :icon="require('@/assets/icons/patient.svg')"
          aria-label="AI Patient Dialogue Options"
          data-testid="ai-patient-dialogue-tab-dropdown"
          :items="patientAiItems"
          :selected="state.activeId === 'PATIENT'"
          :show-rename="!readonly"
          @selectItem="onSelectPatientItem"
          @renameLabel="value => onUpdatePatientLabel(value)"
        />
        <tab-dropdown
          v-for="npc in activeNpcs(caseConf)"
          :key="npc.key"
          :prefix="'AI ' + (npc.name ? '' : npcLabel(npc))"
          :label="npc.name"
          :icon="require('@/assets/icons/npc.svg')"
          :items="npcAiItems"
          :aria-label="`AI ${npcLabel(npc)} Npc Dialogue Options`"
          data-testid="ai-npc-dialogue-options"
          :selected="state.activeId === npc.key"
          :show-rename="!readonly"
          @selectItem="itemKey => onSelectNpcItem(npc.key, itemKey)"
          @renameLabel="value => onUpdateNpcLabel(npc, value)"
        />
      </template>

      <tab-dropdown
        v-for="call in caseConf.dialogue.calls"
        :key="call.id"
        :label="call.callee.en"
        :icon="require('@/assets/icons/callee.svg')"
        :items="calleeItems"
        :aria-label="`${call.callee.en} Callee Dialogue Options`"
        data-testid="callee-dialogue-options"
        :selected="state.activeId === call.id"
        :show-rename="!readonly"
        :show-remove="!readonly"
        :max-length="15"
        @selectItem="itemKey => onSelectCalleeItem(call.id, itemKey)"
        @removeItem="onRemoveCallee(call)"
        @renameLabel="value => onUpdateCalleeLabel(call, value)"
      />

      <div v-if="state.showNewCallInput" md-counter class="new-call-input">
        <inline-editor
          value=""
          :max-length="15"
          :style="{ width: '250px'}"
          @apply="submitNewCall"
          @cancel="cancelNewCall"
        />
      </div>
      <div>
        <md-button
          :disabled="state.showNewCallInput || readonly || (caseConf.dialogue.calls && caseConf.dialogue.calls.length >= 7)"
          class="md-raised md-primary add-call-option-button"
          aria-label="Add New Call Option"
          data-testid="add-new-callee-button"
          @click="showNewCallInput()"
        >
          <md-icon>add</md-icon>Call Option
        </md-button>
        <help-tooltip v-if="(caseConf.dialogue.calls && caseConf.dialogue.calls.length >= 7)" type="warning" aria-label="Max Callee Warning" data-testid="max-callee-warning">
          You can have up to 7 call options
        </help-tooltip>
      </div>
    </div>

    <template>
      <div v-show="state.activeId === 'PATIENT'" class="non-calls-container" data-testid="patient-editor-container">
        <patient-editors
          :active-id="state.activeId"
          :active-tab="state.activeTab"
          :case-conf="caseConf"
          :voices="voices"
          :questions="questions"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          :used-info="usedInfo"
          @update="update"
          @playAnswerText="playAnswerText"
        />
      </div>
      <div v-show="state.activeId === '_startLocation' || state.activeId === '_endLocation'" class="non-calls-container" :data-testid="`npc-editor-container`">
        <npc-editors
          :active-id="state.activeId"
          :active-tab="state.activeTab"
          :case-conf="caseConf"
          :voices="voices"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          :used-info="usedInfo"
          @update="update"
          @playAnswerText="playAnswerText"
        />
      </div>
    </template>

    <div v-show="state.activeId && state.activeId !== 'PATIENT'" class="calls-container" data-testid="callee-container">
      <callee-editors
        :active-id="state.activeId"
        :active-tab="state.activeTab"
        :case-conf="caseConf"
        :voices="voices"
        :questions="questions"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        :used-info="usedInfo"
        @update="update"
        @playAnswerText="playAnswerText"
      />
    </div>

    <md-dialog-confirm
      :md-active.sync="state.callRemovalConfirmOpen"
      md-title="Remove call"
      :md-content="state.pendingRemovalCall ? `Are you sure you want to remove the call option to ${state.pendingRemovalCall.callee.en} ?` : ''"
      md-confirm-text="Remove"
      md-cancel-text="Cancel"
      data-testid="remove-callee-modal-container"
      @md-cancel="cancelPendingCallRemoval"
      @md-confirm="removePendingRemovalCall"
    />
  </panel>
</template>

<script>
import get from 'lodash/get';
import sample from 'lodash/sample';
import Chance from 'chance';

import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import { npcLabel } from '~lib/common/utils/labels';
import { activeNpcs, isNpc } from '~lib/common/utils/npc';

import Panel from '../common/Panel';
import PatientEditors from './PatientEditors.vue';
import NpcEditors from './NpcEditors.vue';
import CalleeEditors from './CalleeEditors.vue';
import TabDropdown from './TabDropdown.vue';
import ToggleSwitch from '../common/ToggleSwitch.vue';
import InlineEditor from '../common/InlineEditor.vue';
import servicesClient from '../../providers/servicesClient';
import DialogueType from './DialogueType';

const chance = new Chance();

const maleVoices = ['OSWALD', 'ROBERT', 'JOE', 'JAMES', 'CALVIN']
const femaleVoices = ['BETH', 'MARY', 'MAYA', 'PENNY', 'CHRISTINE'];

export default {
  name: 'case-questions',
  components: {
    Panel,
    HelpTooltip,
    PatientEditors,
    NpcEditors,
    CalleeEditors,
    TabDropdown,
    ToggleSwitch,
    InlineEditor,
  },
  props: {
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    questions: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
    },
    savingItemId: {
      type: String,
      default: null,
    },
    user: {
      type: Object,
      default: null,
    },
    voices: {
      type: Array,
      default: () => []
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      patientItems: [
        { key: DialogueType.Q_AND_A, label: 'Q & A' },
        { key: DialogueType.COMMANDS, label: 'Commands' },
        { key: DialogueType.UNSOLICITED, label: 'Unsolicited' },
      ],
      patientAiItems: [
        { key: DialogueType.AI_PATIENT_PROMPT, label: 'Prompt' },
        { key: DialogueType.AI_DIRECTED_DIALOGUE, label: 'Listen For/Say This' },
      ],
      npcAiItems: [
        { key: DialogueType.AI_NPC_PROMPT, label: 'Prompt' },
        { key: DialogueType.AI_NPC_DIALOGUE, label: 'Listen For/Say This' },
      ],
      calleeItems: [
        { key: DialogueType.Q_AND_A, label: 'Q & A' },
        { key: DialogueType.UNSOLICITED, label: 'Unsolicited' },
      ],
      npcItems: [
        { key: DialogueType.NPC_Q_AND_A, label: 'Q & A' },
        { key: DialogueType.NPC_COMMANDS, label: 'Commands' },
        { key: DialogueType.NPC_UNSOLICITED_DIALOGUE, label: 'Unsolicited' },
      ],
      state: {
        activeId: 'PATIENT',
        activeTab: this.caseConf.dialogue.aiEnabled ? DialogueType.AI_PATIENT_PROMPT : DialogueType.Q_AND_A,
        showNewCallInput: false,
        pendingRemovalCall: null,
        callRemovalConfirmOpen: false,
      },
      DialogueType,
    };
  },
  computed: {
    errors() {
      const errors = get(
        this.$store.getters['validation/errorsByField'],
        'dialogue',
        []
      );
      return errors;
    },
    warnings() {
      const warnings = get(
        this.$store.getters['validation/visibleWarningsByField'],
        'dialogue',
        []
      );
      return warnings;
    },
    isAiAvailable() {
      return this.user['https://vrpatients.com/aiActive'];
    },
    isAiEnabled() {
      return this.caseConf.dialogue.aiEnabled;
    },
    usedInfo() {
      const list = [
        {
          key: 'PATIENT',
          avatarKey: this.caseConf._avatar.key,
          voiceKey: this.caseConf.voiceKey,
        }
      ];
      for (const npc of this.caseConf.npcs || []) {
        list.push({
          key: npc.key,
          avatarKey: npc.avatarKey,
          voiceKey: npc.voiceKey,
        });
      }
      for (const callee of this.caseConf.dialogue.calls || []) {
        list.push({
          key: callee.id,
          voiceKey: callee.calleeVoiceId,
        });
      }
      return list;
    },
  },
  watch: {
    navigation: {
      handler() {
        this.navigationHandler();
      },
      deep: true
    },
    'caseConf.npcs': {
      handler(newNpcs) {
        if (isNpc(this.state.activeId)) {
          const npc = newNpcs.find(npc => npc.key === this.state.activeId);
          if (!npc) {
            // NPC must have been disabled, switch active to patient
            this.showDefaultTab();
          }
        }
      }
    }
  },
  mounted() {
    this.navigationHandler();
  },
  methods: {
    navigationHandler() {
      const category = this.navigation.category || {};
      const params = this.navigation.params || {};
      const componentId = params.componentId ||  DialogueType.AI_PATIENT_PROMPT;
      const dialogueType = params.dialogueType;
      const characterKey = params.characterKey || 'PATIENT';
      if (category === 'dialogue') {
        if (this.isAiEnabled) {
          if (componentId && dialogueType) {
            this.showTab(characterKey, dialogueType);
          }
          else if (componentId) {
            this.showTab(characterKey, DialogueType.AI_DIRECTED_DIALOGUE);
          } else {
            this.showTab(characterKey, DialogueType.AI_PATIENT_PROMPT);
          }
        } else {
          if (this.navigation.params && dialogueType) {
            this.showTab(characterKey, dialogueType);
          }
        }
      }
    },
    async playAnswerText(question, withVoiceId) {
      const text = question.options.answer[this.caseConf.language];
      const voiceId = withVoiceId || this.caseConf.voiceKey;
      const response = await servicesClient.post('generate-audio', {
        text,
        voiceId,
        // alwaysGenerate: true
      });
      const audio = new Audio(response.data.s3Location);
      audio.play();
    },
    showTab(entityId, dialogueType) {
      this.state.activeTab = dialogueType;
      this.state.activeId = entityId;
    },
    showDefaultTab() {
      this.showTab('PATIENT', this.isAiEnabled ? DialogueType.AI_PATIENT_PROMPT : DialogueType.Q_AND_A);
    },
    update(payload) {
      this.$emit('update', payload);
    },
    showNewCallInput() {
      this.state.showNewCallInput = true;
      this.$nextTick(() => {
        const newCallInputRef = this.$refs.newCallInput;
        if (newCallInputRef && newCallInputRef.$el) {
          newCallInputRef.$el.focus();
        }
      })
    },
    submitNewCall(value) {
      if (!value) {
        return;
      }
      const callId = chance.guid();
      const existingVoices = this.usedInfo.map(info => info.voiceKey);
      const newCall = {
        id: callId,
        callee: {
          en: value,
        },
        calleeVoiceId: this.getRandomVoice(existingVoices),
        items: [],
      };
      this.caseConf.dialogue.calls = this.caseConf.dialogue.calls || [];
      this.caseConf.dialogue.calls.push(newCall)
      this.$emit('update', {
        dialogue: {
          calls: this.caseConf.dialogue.calls,
        },
      });
      this.state.showNewCallInput = false;
      this.showTab(callId, DialogueType.Q_AND_A);
    },
    cancelNewCall() {
      this.state.showNewCallInput = false;
    },
    promptCallRemove(call) {
      this.state.pendingRemovalCall = call;
      this.state.callRemovalConfirmOpen = true;
    },
    removePendingRemovalCall() {
      const { pendingRemovalCall } = this.state;
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).filter(
            (c) => c.id !== pendingRemovalCall.id
          ),
        },
      });
      this.showDefaultTab();
      this.state.callRemovalConfirmOpen = false;
      this.state.pendingRemovalCall = null;
    },
    cancelPendingCallRemoval() {
      this.state.callRemovalConfirmOpen = false;
      this.state.pendingRemovalCall = null;
    },
    onSelectPatientItem(itemKey) {
      this.showTab('PATIENT', itemKey);
    },
    onSelectCalleeItem(callId, itemKey) {
      this.showTab(callId, itemKey);
    },
    onSelectNpcItem(npcId, itemKey) {
      this.showTab(npcId, itemKey);
    },
    onRemoveCallee(call) {
      this.promptCallRemove(call);
    },
    onUpdatePatientLabel(value) {
      if (!value) return;
      this.caseConf.patientName = value;
      this.$emit('update', { patientName: value });
    },
    onUpdateNpcLabel(npc, value) {
      if (!value) return;
      npc.name = value;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    onUpdateCalleeLabel(call, value) {
      if (!value) return;
      call.callee.en = value;
      this.$emit('update', {
        dialogue: {
          calls: this.caseConf.dialogue.calls,
        },
      });
    },
    updateAiEnabled(value) {
      if (this.state.activeId === 'PATIENT') {
        if (value) {
          this.state.activeTab = DialogueType.AI_PATIENT_PROMPT;
        } else {
          this.state.activeTab = DialogueType.Q_AND_A;
        }
      } else if (this.state.activeId === '_startLocation' || this.state.activeId === '_endLocation') {
        if (value) {
          this.state.activeTab = DialogueType.AI_NPC_PROMPT;
        } else {
          this.state.activeTab = DialogueType.NPC_Q_AND_A;
        }
      }
      if (value) {
        this.$analytics.event('Authoring Tool', 'ai_dialogue_enabled', this.user.groupsData[0].name);
      }
      const updates = {
        dialogue: {
          aiEnabled: value,
        }
      };
      if (value) {
        // Set a default prompt so the user won't immediately get errors when turning on AI dialogue
        if (!this.caseConf.dialogue.patientPrompt) {
          const defaultPrompt = 'Write or paste your patient prompt here';
          updates.dialogue.patientPrompt = {
            markup: { en: defaultPrompt },
            content: { en: defaultPrompt }
          }
        }
      }
      this.$emit('update', updates);
    },
    getRandomVoice(excluding) {
      const pool = [...maleVoices, ...femaleVoices].filter(voice => !excluding.includes(voice));
      return sample(pool);
    },
    npcLabel,
    activeNpcs
  },
};
</script>

<style scoped>
.case-questions >>> .panel-content {
  overflow-y: hidden;
}

.case-questions >>> .title-container {
  align-items: flex-start;
}

.tabs-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
}

.md-tabs >>> .md-tabs-navigation .md-button {
  font-size: 16px;
}

.add-call-option-button {
  margin: 0;
  margin-right: 10px;
  border-radius: 16px;
}

.add-call-option-button {
  margin-left: 10px;
}

.non-calls-container {
  height: calc(100% - 100px);
}

.calls-container {
  height: calc(100% - 120px);
}

.md-switch-label.left {
  padding-left: 0;
  padding-right: 16px;
}

.background-blue {
  background-color: #3f83c5;
}

.tab-dropdown {
  margin-right: 8px;
}

.md-title {
  margin-bottom: 8px;
}

.ai-switch-container {
  margin-left: 32px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.ai-switch-container label {
  font-weight: 500;
  font-size: 20px;
}

.ai-description-container {
  display: flex;
  flex-direction: column;
}
</style>
